/** Shared helpers for organization/brand add-edit forms (Sonar duplication). */

export function normalizeOptionalText(
  value: string | null | undefined,
): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : null;
}

export interface AddEditDirtyComparable {
  name: string;
  displayName: string;
  description?: string | null;
  isActive: boolean;
  logoFile?: File | null;
  removeLogo?: boolean;
}

export function isAddEditFormDirty(
  current: AddEditDirtyComparable,
  initial: AddEditDirtyComparable,
): boolean {
  if ((current.name ?? "").trim() !== (initial.name ?? "").trim()) {
    return true;
  }
  if (
    (current.displayName ?? "").trim() !== (initial.displayName ?? "").trim()
  ) {
    return true;
  }
  if (
    normalizeOptionalText(current.description) !==
    normalizeOptionalText(initial.description)
  ) {
    return true;
  }
  if (current.isActive !== initial.isActive) {
    return true;
  }
  if (current.logoFile instanceof File) {
    return true;
  }
  if (current.removeLogo === true) {
    return true;
  }
  return false;
}

/** Default max length for auto-generated organization and brand keys. */
export const ENTITY_KEY_MAX_LENGTH = 25;

/** Strips leading/trailing hyphens in O(n) without regex (Sonar ReDoS-safe). */
export function trimEdgeHyphens(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && value[start] === "-") {
    start += 1;
  }
  while (end > start && value[end - 1] === "-") {
    end -= 1;
  }
  return value.slice(start, end);
}

export function generateEntityKey(
  displayName: string,
  nonKeyCharsPattern: RegExp,
  maxLength: number = ENTITY_KEY_MAX_LENGTH,
): string {
  const key = trimEdgeHyphens(
    displayName
      .trim()
      .toUpperCase()
      .replaceAll(/\s+/g, "-")
      .replaceAll(nonKeyCharsPattern, "")
      .replaceAll(/-+/g, "-"),
  );
  if (key.length <= maxLength) return key;
  return trimEdgeHyphens(key.slice(0, maxLength));
}

export function sanitizeKeyInput(
  raw: string,
  nonKeyCharsPattern: RegExp,
): string {
  return raw
    .toUpperCase()
    .replaceAll(/\s+/g, "")
    .replaceAll(nonKeyCharsPattern, "");
}

export function getInputValue(valueOrEvent: unknown): string {
  if (typeof valueOrEvent === "string") return valueOrEvent;
  if (
    typeof valueOrEvent === "object" &&
    valueOrEvent !== null &&
    "target" in valueOrEvent
  ) {
    const target = (valueOrEvent as { target?: { value?: string } | null })
      .target;
    return target?.value ?? "";
  }
  return "";
}

export function createDuplicateKeyValidator(
  existingKeys: ReadonlySet<string> | undefined,
  excludeKey: string | undefined,
  duplicateMessage: string,
) {
  return (_: unknown, value: string | undefined) => {
    const key = value?.trim().toUpperCase();
    if (!key) return Promise.resolve();
    const exclude = excludeKey?.trim().toUpperCase();
    if (exclude && key === exclude) return Promise.resolve();
    if (existingKeys?.has(key)) {
      return Promise.reject(new Error(duplicateMessage));
    }
    return Promise.resolve();
  };
}

export const LOGO_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/x-png",
  "image/tiff",
  "image/webp",
  "image/gif",
  "image/svg+xml",
  "image/svg",
] as const;

export const LOGO_ACCEPT =
  "image/jpeg,image/jpg,image/png,image/x-png,image/tiff,image/webp,image/gif,image/svg+xml,image/svg";

const LOGO_TYPE_ERROR =
  "Only JPEG, JPG, PNG, TIFF, WebP, GIF, SVG, and SVG+XML files are allowed";

const LOGO_SIZE_ERROR =
  "Logo file size must be less than or equal to 2MB";

export function validateLogoFile(
  _: unknown,
  value: File | null | undefined,
): Promise<void> {
  if (!value) return Promise.resolve();
  if (!LOGO_MIME_TYPES.includes(value.type as (typeof LOGO_MIME_TYPES)[number])) {
    return Promise.reject(new Error(LOGO_TYPE_ERROR));
  }
  if (value.size > 2 * 1024 * 1024) {
    return Promise.reject(new Error(LOGO_SIZE_ERROR));
  }
  return Promise.resolve();
}

export const LOGO_FILE_VALIDATION_RULES = [{ validator: validateLogoFile }];

export const DESCRIPTION_MAX_LENGTH_RULES = [
  {
    max: 200,
    message: "Description must be less than or equal to 200 characters.",
  },
];
